import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectCurrentEmpid, selectCurrentRole } from '../../../pages/login/authSlice';
import { datetimeToDate, formatDate4 } from '../../../utils/dateFormat';
import TrainingCard from './TrainingCard';
import { useGetCourseAssignmentQuery, useGetCoursePastAssignmentQuery } from './courseAssignmentSlice';
import { CourseAssignmentDTO } from './types';
import { CourseTLAssignment } from '../courseTLAssignment/CourseTLAssignment';

export const CourseAssignment = () => {
    const loggedEmpNum = useSelector(selectCurrentEmpid);
    const role = useSelector(selectCurrentRole);
    const [dateRange, setDateRange] = useState<Date[]>([]);
    const [selectedDate, setSelectedDate] = useState<string>('lastYear');
    const rangeOptions = [
        { label: 'Last Week', value: 'lastWeek' },
        { label: 'Last Two Weeks', value: 'last2Weeks' },
        { label: 'Last Month', value: 'lastMonth' },
        { label: 'Last Six Months', value: 'last6Months' },
        { label: 'Last Year', value: 'lastYear' },
    ];

    const { data: CourseAssignmentData } = useGetCourseAssignmentQuery(
        {
            employeeNumber: loggedEmpNum,
            role,
        },
        {
            skip: !loggedEmpNum,
        }
    );

    const { data: coursePastAssignmentData } = useGetCoursePastAssignmentQuery(
        {
            employeeNumber: loggedEmpNum,
            fromDate: datetimeToDate(dateRange[0]),
        },
        {
            skip: !loggedEmpNum.length || !dateRange.length,
        }
    );

    const selectedDateTemplate = (option: any) => {
        if (option) {
            return (
                <div className="flex align-items-center">
                    <i className="pi pi-calendar mr-2" style={{ paddingRight: '5px' }} />
                    {option.label}
                </div>
            );
        }
        return null;
    };

    const handleRangeChange = (e: any) => {
        const value = e.value;
        setSelectedDate(value);
        const calculatedDate = calculateDate(value);
        setDateRange(calculatedDate);
    };
    const calculateDate = (value: string): Date[] => {
        const currentDate = new Date();
        let calculatedDate = new Date();
        switch (value) {
            case 'lastWeek':
                calculatedDate.setDate(currentDate.getDate() - 7);
                break;
            case 'last2Weeks':
                calculatedDate.setDate(currentDate.getDate() - 14);
                break;
            case 'lastMonth':
                calculatedDate.setMonth(currentDate.getMonth() - 1);
                break;
            case 'last6Months':
                calculatedDate.setMonth(currentDate.getMonth() - 6);
                break;
            case 'lastYear':
                calculatedDate.setFullYear(currentDate.getFullYear() - 1);
                break;
            default:
                calculatedDate = currentDate;
                break;
        }
        return [calculatedDate, currentDate];
    };

    useEffect(() => {
        const calculatedDate = calculateDate('lastYear');
        setDateRange(calculatedDate);
    }, []);

    const dateTemplate = (rowData: CourseAssignmentDTO, field: keyof CourseAssignmentDTO) => {
        const dateStr: string = rowData[field]?.toString() || '';
        return dateStr ? formatDate4(dateStr) : '';
    };

    const linkTemplate = (rowData: CourseAssignmentDTO) => {
        return rowData.linkUrl ? (
            <a href={rowData.linkUrl} target="_blank" rel="noopener noreferrer">
                Open Link
            </a>
        ) : null;
    };

    return (
        <div className="flex flex-col gap-4">
            {role === 'agent' ? (
                <div>
                    <div className="mt-3">
                        <h4>My Assigned Courses</h4>
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            gap: '3rem',
                            flexWrap: 'wrap',
                            marginBottom: '2rem',
                            marginTop: '2rem',
                        }}
                    >
                        {CourseAssignmentData?.data?.map(
                            (courseAssignment: any) =>
                                courseAssignment.coachingToDoStatusId !== 3 && (
                                    <TrainingCard
                                        key={courseAssignment.courseAssigned}
                                        metricName={courseAssignment.metric ?? 'MetricName'}
                                        courseName={courseAssignment.courseAssigned ?? 'CourseName'}
                                        skillName={courseAssignment.skill ?? 'Skill'}
                                        behaviorName={courseAssignment.behavior ?? 'BehaviorName'}
                                        assignedDate={
                                            courseAssignment.assignedDate != null
                                                ? formatDate4(courseAssignment.assignedDate)
                                                : ''
                                        }
                                        trainingStatus={
                                            courseAssignment.coachingToDoStatusId === 2 ||
                                            courseAssignment.coachingToDoStatusId === 4
                                                ? 'Continue Training'
                                                : 'Start Training'
                                        }
                                        coachingToDoStatusId={courseAssignment.coachingToDoStatusId ?? 0}
                                        coachingToDoStatus={courseAssignment.coachingToDoStatus ?? ''}
                                        onContinue={() => {
                                            window.open(courseAssignment.linkUrl, '_blank');
                                        }}
                                    />
                                )
                        )}
                    </div>

                    <div className="card">
                        <h5 style={{ margin: 15 }}>My Past Courses</h5>
                        <div className="col-md-3 mb-3">
                            <div className="content" style={{ paddingLeft: '14px;' }}>
                                <label htmlFor="date">Assigned Date</label>
                                <Dropdown
                                    className="custom-dropdown"
                                    value={selectedDate}
                                    options={rangeOptions}
                                    onChange={handleRangeChange}
                                    placeholder="Select Range"
                                    valueTemplate={selectedDateTemplate}
                                    optionLabel="label"
                                    optionValue="value"
                                />
                            </div>
                        </div>
                        <DataTable
                            value={coursePastAssignmentData?.data}
                            paginator
                            rows={10}
                            responsiveLayout="scroll"
                        >
                            <Column field="courseAssigned" header="Course Assigned" />
                            <Column
                                header="Assigned Date"
                                body={(row) => dateTemplate(row, 'assignedDate')}
                            />
                            <Column field="metric" header="Metric" />
                            <Column field="skill" header="Skill" />
                            <Column field="behavior" header="Behavior" />
                            <Column field="teamLead" header="Team Lead" />
                            <Column header="Link" body={linkTemplate} />
                        </DataTable>
                    </div>
                </div>
            ) : (
                <div>
                    <CourseTLAssignment courseAssignments={CourseAssignmentData?.data} />
                    
                </div>
            )}
        </div>
    );
};
